/**
 * Service-worker registration and the PWA install prompt.
 *
 * The worker registers in production builds only. In dev it would fight
 * Vite's module server and serve yesterday's app - so there, any previously
 * registered worker is actively unregistered instead, which un-wedges a
 * browser that once visited a production/preview build on this origin.
 *
 * Registration also requires a secure context: HTTPS, or localhost. Over
 * plain http on a LAN IP (http://192.168.x.x:5173) browsers refuse service
 * workers entirely - the app still works, it just is not installable there.
 *
 * Install prompt: Chromium fires `beforeinstallprompt` once the app qualifies
 * for installation - often before React has mounted anything, which is why it
 * is captured here at startup rather than inside a component. The event is
 * stashed and surfaced through a tiny external store; `InstallAppButton`
 * subscribes via useSyncExternalStore and calls `promptInstall()` on click.
 * Browsers without the event (Safari, Firefox) simply never show the button.
 */

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener());
}

export function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari's pre-standard flag for "launched from home screen".
    window.navigator.standalone === true
  );
}

export function subscribeInstall(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Snapshot for useSyncExternalStore - a primitive, so referential equality works. */
export function canInstall() {
  return Boolean(deferredPrompt) && !isStandalone();
}

/**
 * Show the browser's install dialog. Returns 'accepted' | 'dismissed' |
 * 'unavailable'. A stashed event is single-use: after a dismissal the button
 * disappears until the browser decides to offer installation again.
 */
export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';
  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  notify();
  promptEvent.prompt();
  const { outcome } = await promptEvent.userChoice;
  return outcome;
}

/**
 * Handle user request to install the app.
 * Returns: 'accepted' | 'dismissed' | 'already_installed' | 'ios' | 'manual'
 */
export async function triggerInstall() {
  if (isStandalone()) {
    return 'already_installed';
  }
  if (deferredPrompt) {
    return await promptInstall();
  }
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (isIos) {
    return 'ios';
  }
  return 'manual';
}

export function setupPwa() {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Stop Chrome's own mini-infobar; the header button is the install UI.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });

  if (!('serviceWorker' in navigator)) return;

  const registerSw = () => {
    navigator.serviceWorker
      .register('/sw.js')
      .catch((error) => console.warn('Service worker registration failed:', error));
  };

  if (document.readyState === 'complete') {
    registerSw();
  } else {
    window.addEventListener('load', registerSw);
  }
}
